import CodeMirror from "codemirror";
import "codemirror/addon/mode/simple";
import "codemirror/keymap/sublime";
import "codemirror/lib/codemirror.css";
import "codemirror/theme/ambiance.css";
import "codemirror/theme/material-palenight.css";
import "codemirror/theme/material-darker.css";
import "codemirror/theme/material-ocean.css";
import "codemirror/theme/paraiso-dark.css";
import "codemirror/theme/pastel-on-dark.css";
import "codemirror/theme/neat.css";
import React, { useState } from "react";
import { Controlled as CodeMirror2 } from "react-codemirror2-react-17";
import { EditorProps, EditorMode } from "./types";
import { CustomThemeOptions } from "../../CustomColors/Colors";
import { Alert, useTheme } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

const mode: EditorMode = "cairo";

CodeMirror.defineSimpleMode(mode, {
    start: [
        {
            regex: /(?:func|struct|namespace|end|call|ret|jmp|if|let|const|import|from|as|abs|rel|local|static_assert|tempvar|return|assert|member|felt|cast|else|alloc_locals|with|nondet|with_attr)\b/,
            token: "keyword",
        },
        { regex: /ap|fp/, token: "atom" },
        {
            regex: /-?[0-9]+/,
            token: "number",
        },
        { regex: /[/][/].*/, token: "comment" },
        { regex: /[-+*=<>!]+/, token: "operator" },
        { regex: /[a-zA-Z_][a-zA-Z_0-9]*/, token: "variable" },
        {
            regex: /%{/,
            token: "meta",
            mode: { spec: "python", end: /%}/ },
        },
    ],
    meta: {
        dontIndentStates: ["comment"],
        lineComment: "#",
    },
});

const Editor = ({
    isReadOnly,
    content,
    onChange,
    severity,
    message,
}: EditorProps) => {
    const theme: CustomThemeOptions = useTheme();
    const [open, setOpen] = useState<boolean>(true);
    const editorOptions: CodeMirror.EditorConfiguration = {
        theme: theme.palette.editor,
        mode: "cairo",
        viewportMargin: 100,
        keyMap: "sublime",
        lineNumbers: true,
        tabSize: 4,
        indentWithTabs: false,
        indentUnit: 4,
        extraKeys: {
            Tab: function (cm: any) {
                if (cm.somethingSelected()) {
                    cm.indentSelection("add");
                } else {
                    const spaces = Array(cm.getOption("indentUnit") + 1).join(
                        " "
                    );
                    cm.replaceSelection(spaces);
                }
            },
            "Ctrl-Space": "autocomplete",
        },
        readOnly: isReadOnly,
        showCursorWhenSelecting: true,
    };

    const renderActionButton = () => {
        return (
            <IconButton
                color="inherit"
                size="small"
                onClick={() => {
                    setOpen(false);
                }}
            ></IconButton>
        );
    };

    const { innerHeight } = window;

    return (
        <div
            style={{
                width: "100%",
                border: "1px solid black",
                maxWidth: "800px",
            }}
        >
            <CodeMirror2
                options={editorOptions}
                value={content}
                onBeforeChange={(editor, data, value) => {
                    onChange(value);
                }}
                editorDidMount={(editor) => {
                    editor.setSize("", innerHeight * 0.7);
                }}
            />
            {severity ? (
                <Alert
                    variant="filled"
                    severity={severity}
                    action={renderActionButton()}
                >
                    {message}
                </Alert>
            ) : (
                <></>
            )}
        </div>
    );
};

export default Editor;
